import { Badge } from "@gotts.ai/ui";

import type { Address } from "viem";

/**
 * V4 hook permission flags derived from the last 2 bytes of the hook address.
 * Bit layout (from Uniswap V4 Hooks.sol):
 *   bit 15 (0x8000): BEFORE_INITIALIZE_FLAG
 *   bit 14 (0x4000): AFTER_INITIALIZE_FLAG
 *   bit 13 (0x2000): BEFORE_ADD_LIQUIDITY_FLAG
 *   bit 12 (0x1000): AFTER_ADD_LIQUIDITY_FLAG
 *   bit 11 (0x0800): BEFORE_REMOVE_LIQUIDITY_FLAG
 *   bit 10 (0x0400): AFTER_REMOVE_LIQUIDITY_FLAG
 *   bit 9  (0x0200): BEFORE_SWAP_FLAG
 *   bit 8  (0x0100): AFTER_SWAP_FLAG
 *   bit 7  (0x0080): BEFORE_DONATE_FLAG
 *   bit 6  (0x0040): AFTER_DONATE_FLAG
 *   bit 5  (0x0020): BEFORE_SWAP_RETURNS_DELTA_FLAG
 *   bit 4  (0x0010): AFTER_SWAP_RETURNS_DELTA_FLAG
 *   bit 3  (0x0008): AFTER_ADD_LIQUIDITY_RETURNS_DELTA_FLAG
 *   bit 2  (0x0004): AFTER_REMOVE_LIQUIDITY_RETURNS_DELTA_FLAG
 */
const HOOK_FLAGS = [
  { bit: 15, label: "beforeInitialize" },
  { bit: 14, label: "afterInitialize" },
  { bit: 13, label: "beforeAddLiquidity" },
  { bit: 12, label: "afterAddLiquidity" },
  { bit: 11, label: "beforeRemoveLiquidity" },
  { bit: 10, label: "afterRemoveLiquidity" },
  { bit: 9, label: "beforeSwap" },
  { bit: 8, label: "afterSwap" },
  { bit: 7, label: "beforeDonate" },
  { bit: 6, label: "afterDonate" },
  { bit: 5, label: "beforeSwapReturnDelta" },
  { bit: 4, label: "afterSwapReturnDelta" },
  { bit: 3, label: "afterAddLiqReturnDelta" },
  { bit: 2, label: "afterRemoveLiqReturnDelta" },
] as const;

export function extractHookFlags(hookAddress: Address): Map<string, boolean> {
  const flags = new Map<string, boolean>();
  const addressNum = BigInt(hookAddress);
  // Last 2 bytes = last 4 hex chars = bottom 16 bits
  const bottom16 = Number(addressNum & 0xffffn);
  for (const { bit, label } of HOOK_FLAGS) {
    flags.set(label, (bottom16 & (1 << bit)) !== 0);
  }
  return flags;
}

interface HookFlagsGridProps {
  hookAddress: Address;
}

export function HookFlagsGrid({ hookAddress }: HookFlagsGridProps) {
  const flags = extractHookFlags(hookAddress);
  const activeFlags = HOOK_FLAGS.filter((f) => flags.get(f.label));
  const inactiveFlags = HOOK_FLAGS.filter((f) => !flags.get(f.label));

  if (activeFlags.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">No hook flags enabled</p>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs font-medium">
        Hook Flags ({activeFlags.length}/{HOOK_FLAGS.length})
      </p>
      <div className="flex flex-wrap gap-1">
        {activeFlags.map(({ label }) => (
          <Badge key={label} variant="info" className="px-1.5 py-0 text-[10px]">
            {label}
          </Badge>
        ))}
        {inactiveFlags.map(({ label }) => (
          <Badge
            key={label}
            variant="info"
            className="px-1.5 py-0 text-[10px] opacity-30"
          >
            {label}
          </Badge>
        ))}
      </div>
    </div>
  );
}
